import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Divider,
  Paper,
  TextField,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

const normalizeTargetSchema = (targetSchema) => {
  if (!targetSchema) return [];
  if (Array.isArray(targetSchema)) return targetSchema;
  return [{ tableName: targetSchema, columns: [], id: targetSchema }];
};

const parseSourceColumn = (sourceColumn) => {
  if (!sourceColumn) return { table: '', column: '' };
  const parts = sourceColumn.split('.');
  if (parts.length === 2) {
    return { table: parts[0], column: parts[1] };
  }
  return { table: '', column: sourceColumn };
};

const ConcatMappingDialog = ({
  open,
  onClose,
  sources = [],
  targetSchema,
  fetchedHeader = [],
  initialValues = null,
  existingMappings = [],
  onSave,
  reviewMode = false,
  onReject,
  onApprove,
}) => {
  const targets = useMemo(() => normalizeTargetSchema(targetSchema), [targetSchema]);

  const [sourceTable, setSourceTable] = useState('');
  const [concatColumns, setConcatColumns] = useState([]);
  const [delimiter, setDelimiter] = useState(' ');
  const [targetTable, setTargetTable] = useState('');
  const [targetColumn, setTargetColumn] = useState('');

  useEffect(() => {
    if (open && initialValues) {
      let sourceTableValue = '';
      if (initialValues.sourceTable) {
        sourceTableValue = initialValues.sourceTable;
      } else if (initialValues.left) {
        sourceTableValue = initialValues.left;
      } else if (initialValues.source_columns && initialValues.source_columns.length > 0) {
        const firstSource = parseSourceColumn(initialValues.source_columns[0]);
        sourceTableValue = firstSource.table;
      }
      setSourceTable(sourceTableValue);

      let concatCols = [];
      if (initialValues.concatColumns && Array.isArray(initialValues.concatColumns)) {
        concatCols = initialValues.concatColumns;
      } else if (initialValues.source_columns && initialValues.source_columns.length > 0) {
        concatCols = initialValues.source_columns.map((col) => {
          const parsed = parseSourceColumn(col);
          return parsed.column || col;
        });
      }
      setConcatColumns(concatCols);

      if (initialValues.delimiter !== undefined) {
        setDelimiter(initialValues.delimiter);
      } else {
        setDelimiter(' ');
      }

      if (initialValues.destinationName) {
        setTargetTable(initialValues.destinationName);
      } else if (initialValues.destination_name) {
        setTargetTable(initialValues.destination_name);
      } else if (initialValues.right) {
        setTargetTable(initialValues.right);
      }

      if (initialValues.targetColumn) {
        setTargetColumn(initialValues.targetColumn);
      } else if (initialValues.target_column) {
        setTargetColumn(initialValues.target_column);
      } else if (initialValues.rightKey) {
        setTargetColumn(initialValues.rightKey);
      }
    } else if (open && !initialValues) {
      setSourceTable('');
      setConcatColumns([]);
      setDelimiter(' ');
      setTargetTable('');
      setTargetColumn('');
    }
  }, [open, initialValues]);

  const sourceTableOptions = useMemo(
    () => sources.map((s) => ({ value: s.name || s.id, label: s.name || s.id })),
    [sources]
  );

  const sourceColumns = useMemo(() => {
    if (!sourceTable) return [];
    const src = sources.find((s) => (s.name || s.id) === sourceTable);
    const cols = src?.columns || [];
    return Array.isArray(cols) ? cols.map((c) => ({ value: c, label: c })) : [];
  }, [sources, sourceTable]);

  const targetTableOptions = useMemo(
    () => targets.map((t) => ({ value: t.tableName || t.id, label: t.tableName || t.id })),
    [targets]
  );

  const mappedTargetColsForTable = useMemo(() => {
    if (!targetTable || !Array.isArray(existingMappings)) return new Set();
    return new Set(
      existingMappings
        .filter((m) => (m.destination_name || m.destinationName) === targetTable)
        .map((m) => (m.target_column || m.targetColumn || '').trim())
        .filter(Boolean)
    );
  }, [targetTable, existingMappings]);

  const targetColumnOptions = useMemo(() => {
    if (!targetTable) return [];
    const target = targets.find((t) => (t.tableName || t.id) === targetTable);
    const normalizeCol = (c) => {
      if (c == null) return '';
      if (typeof c === 'string') return c;
      return c.column_name ?? c.name ?? c.columnName ?? '';
    };
    let opts = [];
    if (target?.columns?.length) {
      opts = target.columns
        .map((c) => {
          const name = normalizeCol(c);
          return { value: name, label: name };
        })
        .filter((opt) => opt.value != null && String(opt.value).trim() !== '');
    }
    if (opts.length === 0) {
      opts = (fetchedHeader || [])
        .filter((h) => h.sourceTable === targetTable)
        .map((h) => ({ value: h.columnName ?? h.column_name, label: h.columnName ?? h.column_name }))
        .filter((opt) => opt.value != null && opt.value !== '');
    }
    const currentSelection = initialValues?.targetColumn ?? initialValues?.target_column ?? targetColumn;
    return opts.filter(
      (opt) => !mappedTargetColsForTable.has(opt.value) || opt.value === currentSelection
    );
  }, [targets, targetTable, fetchedHeader, mappedTargetColsForTable, initialValues?.targetColumn, initialValues?.target_column, targetColumn]);

  const availableColumns = useMemo(() => {
    return sourceColumns.filter((col) => !concatColumns.includes(col.value));
  }, [sourceColumns, concatColumns]);

  const handleAddColumn = (columnValue) => {
    if (columnValue && !concatColumns.includes(columnValue)) {
      setConcatColumns([...concatColumns, columnValue]);
    }
  };

  const handleRemoveColumn = (columnValue) => {
    setConcatColumns(concatColumns.filter((col) => col !== columnValue));
  };

  const handleClose = () => {
    setSourceTable('');
    setConcatColumns([]);
    setDelimiter(' ');
    setTargetTable('');
    setTargetColumn('');
    onClose();
  };

  const handleSave = () => {
    if (!sourceTable || concatColumns.length < 2 || !targetTable || !targetColumn) {
      return;
    }

    const formattedSourceColumns = concatColumns.map((col) => `${sourceTable}.${col}`);

    const payload = {
      left: sourceTable,
      right: targetTable,
      leftKey: concatColumns[0],
      rightKey: targetColumn,
      destinationName: targetTable,
      targetColumn,
      sourceTable,
      concatColumns,
      delimiter,
      mapping_type: 'CONCAT',
      mappingType: 'CONCAT',
      source_columns: formattedSourceColumns,
    };

    if (initialValues) {
      if (initialValues.confidence) payload.confidence = initialValues.confidence;
      if (initialValues.mapping_state) payload.mapping_state = initialValues.mapping_state;
      if (initialValues.mappingState) payload.mappingState = initialValues.mappingState;
      if (initialValues.possible_matches) payload.possible_matches = initialValues.possible_matches;
      if (initialValues.possibleMatches) payload.possibleMatches = initialValues.possibleMatches;
      if (initialValues.reasoning) payload.reasoning = initialValues.reasoning;
      if (initialValues.id) payload.id = initialValues.id;
    }

    onSave?.(payload);
    handleClose();
  };

  const canSave = sourceTable && concatColumns.length >= 2 && targetTable && targetColumn;

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#fafafa',
      '&:hover': { backgroundColor: '#f5f5f5' },
      '&.Mui-focused': { backgroundColor: '#fff' },
    },
  };

  const getPreviewValue = () => {
    if (concatColumns.length === 0) return 'Select columns to concatenate';
    return concatColumns.map((col) => `[${col}]`).join(delimiter);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.25rem',
          color: '#1f2937',
          borderBottom: '1px solid #e5e7eb',
          py: 2.5,
          px: 3,
          backgroundColor: '#fafafa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{initialValues ? 'Edit Concat Mapping' : 'New Concat Mapping'}</span>
        {initialValues?.mapping_state && (
          <Chip
            label={initialValues.mapping_state.replace('_', ' ').toUpperCase()}
            size="small"
            sx={{
              backgroundColor:
                initialValues.mapping_state === 'auto_merge'
                  ? '#e3f2fd'
                  : initialValues.mapping_state === 'pending'
                    ? '#fff3e0'
                    : '#f5f5f5',
              color:
                initialValues.mapping_state === 'auto_merge'
                  ? '#1976d2'
                  : initialValues.mapping_state === 'pending'
                    ? '#ed6c02'
                    : '#1f2937',
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          />
        )}
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Stack spacing={3}>
          {/* Source Section */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: '0.08em',
                fontSize: '0.7rem',
                display: 'block',
                mb: 1.5,
              }}
            >
              SOURCE
            </Typography>

            {/* Source Table */}
            <FormControl size="small" fullWidth sx={{ mb: 2.5, ...selectSx }}>
              <InputLabel>Source table</InputLabel>
              <Select
                value={sourceTable}
                label="Source table"
                onChange={(e) => {
                  setSourceTable(e.target.value);
                  setConcatColumns([]);
                }}
                disabled={reviewMode}
              >
                {sourceTableOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Columns to Concatenate */}
            <Box>
              {/* Selected Columns */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {concatColumns.length === 0 ? (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
                    No columns selected yet
                  </Typography>
                ) : (
                  concatColumns.map((col) => (
                    <Chip
                      key={col}
                      label={col}
                      onDelete={() => handleRemoveColumn(col)}
                      sx={{
                        backgroundColor: '#00043c',
                        color: '#fff',
                        fontWeight: 600,
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': {
                            color: '#fff',
                          },
                        },
                      }}
                    />
                  ))
                )}
              </Box>

              {/* Add Column Dropdown */}
              {sourceTable && availableColumns.length > 0 && (
                <FormControl size="small" fullWidth sx={selectSx}>
                  <InputLabel>Add a column</InputLabel>
                  <Select
                    value=""
                    label="Add a column"
                    onChange={(e) => {
                      handleAddColumn(e.target.value);
                    }}
                    disabled={reviewMode}
                    renderValue={() => ''}
                    displayEmpty
                  >
                    {availableColumns.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AddIcon fontSize="small" sx={{ color: '#00043c' }} />
                          {opt.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {sourceTable && availableColumns.length === 0 && concatColumns.length > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                  All columns added
                </Typography>
              )}

              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                Select at least 2 columns to concatenate
              </Typography>
            </Box>

            {/* Delimiter */}
            <Box sx={{ mt: 2.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937', mb: 1 }}>
                Delimiter
              </Typography>
              <TextField
                size="small"
                value={delimiter}
                onChange={(e) => setDelimiter(e.target.value)}
                placeholder="e.g., space, comma, hyphen"
                fullWidth
                disabled={reviewMode}
                sx={selectSx}
                helperText="Character(s) to insert between values (e.g., ' ' for space, ', ' for comma+space)"
              />
            </Box>
          </Box>

          {/* Divider */}
          <Divider>
            <Chip
              label="MAP TO"
              size="small"
              sx={{
                borderRadius: 1.5,
                backgroundColor: '#f0f0f0',
                color: '#1f2937',
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
            />
          </Divider>

          {/* Target Section */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: '0.08em',
                fontSize: '0.7rem',
                display: 'block',
                mb: 1.5,
              }}
            >
              TARGET
            </Typography>

            <Stack spacing={2}>
              <FormControl size="small" fullWidth sx={selectSx} disabled={reviewMode}>
                <InputLabel>Target table</InputLabel>
                <Select
                  value={targetTable}
                  label="Target table"
                  onChange={(e) => {
                    setTargetTable(e.target.value);
                    setTargetColumn('');
                  }}
                  disabled={reviewMode}
                >
                  {targetTableOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth sx={selectSx} disabled={reviewMode || !targetTable}>
                <InputLabel>Target column</InputLabel>
                <Select
                  value={targetColumn ?? ''}
                  label="Target column"
                  onChange={(e) => setTargetColumn(e.target.value)}
                  disabled={reviewMode || !targetTable}
                >
                  {targetTable && targetColumnOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      No columns available
                    </MenuItem>
                  ) : (
                    targetColumnOptions.map((opt, idx) => (
                      <MenuItem key={`targetcol-${idx}-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {/* Preview */}
          {canSave && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#f9f9f9',
                border: '1px dashed #e0e0e0',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1.5 }}>
                PREVIEW
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Chip label={sourceTable} size="small" sx={{ backgroundColor: '#e3f2fd', fontWeight: 600 }} />
                <Typography variant="body2" sx={{ color: '#1f2937' }}>
                  →
                </Typography>
                {concatColumns.map((col, idx) => (
                  <React.Fragment key={col}>
                    <Chip label={col} size="small" sx={{ backgroundColor: '#00043c', color: '#fff', fontWeight: 600 }} />
                    {idx < concatColumns.length - 1 && (
                      <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 600, fontFamily: 'monospace' }}>
                        {delimiter === ' ' ? '␣' : delimiter}
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
                <Typography variant="body2" sx={{ color: '#1f2937' }}>
                  →
                </Typography>
                <Chip
                  label={`${targetTable}.${targetColumn}`}
                  size="small"
                  sx={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ p: 1.5, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Example output:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1f2937' }}>
                  {getPreviewValue()}
                </Typography>
              </Box>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          gap: 1.5,
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#fafafa',
        }}
      >
        {reviewMode ? (
          <>
            <Button onClick={onReject} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' } }}>
              Reject
            </Button>
            <Button onClick={onApprove} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, bgcolor: '#16a34a', color: '#fff', '&:hover': { bgcolor: '#15803d' } }}>
              Approve
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} variant="outlined" color="inherit" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={!canSave} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, bgcolor: '#00043c', color: '#fff', boxShadow: '0 2px 8px rgba(0, 4, 60, 0.35)', '&:hover': { bgcolor: '#000335', boxShadow: '0 4px 12px rgba(0, 4, 60, 0.4)' }, '&:disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' } }}>
              {initialValues ? 'Update' : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConcatMappingDialog;